import React, { useState, useCallback, useMemo } from 'react';
import { css, keyframes } from '@emotion/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeRaw from 'rehype-raw';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { colors, spacing, fontStyles } from '../../../styles';
import { useStreamingChat } from '../../../hooks/useStreamingChat';
import LoadingMessage from '../../LoadingMessage';
import ToolOutputDisplay from '../../ChatPage/ToolOutputDisplay';
import { cleanMarkdownContent } from '../../../utils/contentCleaning';
import { ToolOutputEvent } from '../../../types/streaming';
import { trackTryBloomQuestionAsked, trackTryBloomResponseReceived } from '../../../utils/analytics';

// Message type for conversation history
interface ConversationMessage {
  role: 'user' | 'assistant';
  content: string;
  toolOutputs?: ToolOutputEvent[];
}

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

interface TryBloomConversationProps {
  isDarkMode: boolean;
  messages: ConversationMessage[];
  isLoading: boolean;
  progressText: string;
  streamingMessage: string;
  currentResponse: string | null;
  toolOutputs: ToolOutputEvent[];
  latestTodoOutput: ToolOutputEvent | null;
}

// Markdown renderer component matching ChatPage
const MarkdownContent: React.FC<{ content: string; isDarkMode: boolean }> = ({ content, isDarkMode }) => {
  const displayContent = content.replace(/(?<!\[)\$([A-Z]+)/g, '[$$$1](/symbol/$1)');

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm, [remarkMath, { singleDollarTextMath: false }]]}
      rehypePlugins={[rehypeRaw as any, rehypeKatex as any]}
      components={{
        table: ({ children, ...props }) => (
          <div className="table-wrapper">
            <table {...props}>{children}</table>
          </div>
        ),
        td: ({ children, ...props }) => <td {...props}>{children}</td>,
        th: ({ children, ...props }) => <th {...props}>{children}</th>,
        a: ({ href, children, ...props }) => {
          if (href?.startsWith('/symbol/')) {
            return (
              <span
                css={css`
                  color: ${isDarkMode ? colors.skyBlue : colors.petrolBlue};
                  font-weight: 500;
                `}
              >
                {children}
              </span>
            );
          }
          const isExternal = href?.startsWith('http');
          if (isExternal) {
            return (
              <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                {children}
              </a>
            );
          }
          return (
            <a href={href} {...props}>
              {children}
            </a>
          );
        },
      }}
    >
      {cleanMarkdownContent(displayContent)}
    </ReactMarkdown>
  );
};

const TryBloomConversation: React.FC<TryBloomConversationProps> = ({
  isDarkMode,
  messages,
  isLoading,
  progressText,
  streamingMessage,
  currentResponse,
  toolOutputs,
  latestTodoOutput,
}) => {
  // Combine tool outputs
  const allToolOutputs: ToolOutputEvent[] = [];
  if (latestTodoOutput) allToolOutputs.push(latestTodoOutput);
  allToolOutputs.push(...toolOutputs);

  // Match ChatPage markdown styles
  const markdownStyles = css`
    ${fontStyles.dog}
    color: ${isDarkMode ? colors.sugar : colors.liquorice};
    -webkit-user-select: text;
    user-select: text;

    /* Markdown styles */
    & p {
      margin: 0 0 ${spacing.centi} 0;
      &:last-child {
        margin-bottom: 0;
      }
    }
    & h1 {
      ${fontStyles.lion}
      margin: 0 0 ${spacing.centi} 0;
    }
    & h2 {
      ${fontStyles.dog}
      margin: 0 0 ${spacing.centi} 0;
    }
    & ul,
    & ol {
      margin: ${spacing.centi} 0;
      padding-left: ${spacing.regular};
    }
    & li {
      margin: ${spacing.milli} 0;
    }
    & code {
      background-color: rgba(0, 0, 0, 0.1);
      padding: 2px 4px;
      border-radius: 4px;
    }
    & pre {
      background-color: rgba(0, 0, 0, 0.1);
      padding: ${spacing.milli};
      border-radius: 4px;
      overflow-x: auto;
      margin: ${spacing.milli} 0;
    }
    & strong {
      font-weight: 500;
    }
    & em {
      font-style: normal;
    }
    & a {
      color: ${isDarkMode ? colors.skyBlue : colors.petrolBlue};
      text-decoration: underline;
    }

    /* Table styles */
    & .table-wrapper {
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
      margin: ${spacing.deci} 0;
      border-radius: 8px;
      border: 1px solid ${colors.cloud};
    }

    & table {
      border-collapse: collapse;
      width: 100%;
      display: table;
      font-size: 0.9em;
      margin: 0;
    }

    & th,
    & td {
      border: none;
      border-bottom: 1px solid ${colors.cloud};
      padding: ${spacing.deci};
      text-align: left;
      vertical-align: top;
      white-space: normal;
      word-break: normal;
      line-height: 1.5;
    }

    & th {
      background-color: rgba(0, 0, 0, 0.05);
      font-weight: 600;
      min-width: 120px;
      white-space: nowrap;
      border-bottom: 2px solid ${colors.cloud};
    }

    & td {
      white-space: pre-line;
      & br {
        display: block;
        content: '';
        margin-top: 0.5em;
      }
      & ul,
      & ol {
        margin: 0.5em 0;
        padding-left: 1.5em;
      }
      & li {
        margin: 0.25em 0;
      }
      & strong {
        font-weight: 500;
      }
      & em {
        font-style: normal;
      }
    }

    & tr:nth-of-type(even) {
      background-color: rgba(0, 0, 0, 0.02);
    }

    & tr:last-child td {
      border-bottom: none;
    }

    html[data-theme='dark'] & {
      & .table-wrapper {
        border-color: ${colors.tungsten};
      }
      & th,
      & td {
        border-color: ${colors.tungsten};
      }
      & th {
        background-color: rgba(255, 255, 255, 0.05);
        border-bottom-color: ${colors.tungsten};
      }
      & tr:nth-of-type(even) {
        background-color: rgba(255, 255, 255, 0.02);
      }
    }
  `;

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: ${spacing.deci};
      `}
    >
      {/* Render conversation history */}
      {messages.map((message, index) => (
        <div key={index}>
          {message.role === 'user' ? (
            <div
              css={css`
                display: flex;
                justify-content: flex-end;
              `}
            >
              <div
                css={css`
                  background: ${colors.petrolBlue};
                  color: ${colors.sugar};
                  padding: ${spacing.deci};
                  border-radius: 12px;
                  max-width: 85%;
                  ${fontStyles.dog}
                `}
              >
                {message.content}
              </div>
            </div>
          ) : (
            <div css={markdownStyles}>
              {message.toolOutputs && message.toolOutputs.length > 0 && (
                <div
                  css={css`
                    margin-bottom: ${spacing.deci};
                  `}
                >
                  {message.toolOutputs.map((toolOutput, idx) => (
                    <ToolOutputDisplay key={idx} toolOutput={toolOutput} />
                  ))}
                </div>
              )}
              <MarkdownContent content={message.content} isDarkMode={isDarkMode} />
            </div>
          )}
        </div>
      ))}

      {/* Show loading state for current query */}
      {isLoading && (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: ${spacing.deci};
          `}
        >
          {allToolOutputs.length > 0 && (
            <div>
              {allToolOutputs.map((toolOutput, idx) => (
                <ToolOutputDisplay
                  key={idx}
                  toolOutput={toolOutput}
                  showLatestBadge={toolOutput === latestTodoOutput}
                />
              ))}
            </div>
          )}
          <LoadingMessage progressText={progressText} streamingMessage={streamingMessage} textSize="normal" />
        </div>
      )}

      {/* Show current response being streamed (before it's added to messages) */}
      {!isLoading && currentResponse && messages.length > 0 && messages[messages.length - 1].role === 'user' && (
        <div css={markdownStyles}>
          {allToolOutputs.length > 0 && (
            <div
              css={css`
                margin-bottom: ${spacing.deci};
              `}
            >
              {allToolOutputs.map((toolOutput, idx) => (
                <ToolOutputDisplay key={idx} toolOutput={toolOutput} />
              ))}
            </div>
          )}
          <MarkdownContent content={currentResponse} isDarkMode={isDarkMode} />
        </div>
      )}
    </div>
  );
};

interface TryBloomStepProps {
  isDarkMode?: boolean;
  userSymbols: string[];
  onQuestionAsked?: () => void;
  onStateChange?: (state: { hasAskedQuestion: boolean; hasResponse: boolean }) => void;
  /** When provided, this question is shown as the first suggested question (context-aware pre-load) */
  initialSuggestedQuestion?: string;
}

type StepState = 'questions' | 'loading' | 'response';

// Question pool from ChatPage empty state - defined outside component for stable reference
const QUESTION_POOL = [
  'What can I do with Bloom?',
  'Summarize the latest news for Netflix',
  'What could I invest in?',
  'Is Apple a good buy?',
  "Analyze NVIDIA's price chart",
  "What's the current market sentiment?",
  'Find earnings call mentions of AI revenue growth',
  "Analyze Microsoft's latest earnings results",
  'What do the technical indicators show for Amazon?',
  'Find stocks with less than 15 P/E',
  'Give me investing ideas',
  'What are potential catalysts for Netflix?',
  "Show me today's top movers",
  'What sectors are performing best this week?',
  'How are global markets doing?',
];

const TryBloomStep: React.FC<TryBloomStepProps> = ({
  isDarkMode = false,
  userSymbols,
  onQuestionAsked,
  onStateChange,
  initialSuggestedQuestion,
}) => {
  const [stepState, setStepState] = useState<StepState>('questions');
  const [customQuestion, setCustomQuestion] = useState<string>('');
  const [conversationHistory, setConversationHistory] = useState<ConversationMessage[]>([]);
  const questionStartTime = React.useRef<number>(0);
  const hasTrackedResponse = React.useRef(false);
  const lastProcessedResponse = React.useRef<string | null>(null);

  // Use the streaming chat hook with all values
  const {
    isLoading,
    progressText,
    streamingMessage,
    finalResponse,
    toolOutputs,
    latestTodoOutput,
    submitQuery,
    resetQuery,
  } = useStreamingChat();

  // Randomly select 3 questions (memoized to stay stable during component lifetime)
  // If initialSuggestedQuestion is provided, it is shown first; remaining 2 from the pool.
  const initialSuggestedQuestions = useMemo(() => {
    const shuffled = [...QUESTION_POOL].sort(() => Math.random() - 0.5);
    if (initialSuggestedQuestion) {
      return [initialSuggestedQuestion, ...shuffled.slice(0, 2)];
    }
    return shuffled.slice(0, 3);
  }, [initialSuggestedQuestion]);

  // Track primary symbol for analytics (even though questions aren't symbol-based)
  const primarySymbol = userSymbols[0] || 'NVDA';

  const handleQuestionTap = useCallback(
    async (question: string, isCustom = false) => {
      // Add user message to conversation history
      const newUserMessage: ConversationMessage = { role: 'user', content: question };
      setConversationHistory((prev) => [...prev, newUserMessage]);

      setStepState('loading');
      setCustomQuestion('');
      questionStartTime.current = Date.now();
      hasTrackedResponse.current = false;

      // Track only the first question
      if (conversationHistory.length === 0) {
        trackTryBloomQuestionAsked({
          question,
          questionType: isCustom ? 'custom' : 'suggested',
          primarySymbol,
          totalSymbols: userSymbols.length,
        });
      }

      // Build API messages from full conversation history
      const allMessages = [...conversationHistory, newUserMessage];
      const apiMessages = allMessages.map((m) => ({ role: m.role as 'user' | 'assistant', content: m.content }));
      await submitQuery(apiMessages);

      if (onQuestionAsked && conversationHistory.length === 0) {
        onQuestionAsked();
      }
    },
    [submitQuery, onQuestionAsked, primarySymbol, userSymbols.length, conversationHistory],
  );

  const handleCustomSubmit = useCallback(() => {
    if (customQuestion.trim()) {
      handleQuestionTap(customQuestion.trim(), true);
    }
  }, [customQuestion, handleQuestionTap]);

  // Update state when response is ready and add to conversation history
  React.useEffect(() => {
    if (finalResponse && !isLoading && finalResponse !== lastProcessedResponse.current) {
      lastProcessedResponse.current = finalResponse;

      // Add assistant message to conversation history (include tool outputs)
      const allToolOutputsToSave: ToolOutputEvent[] = [];
      if (latestTodoOutput) allToolOutputsToSave.push(latestTodoOutput);
      allToolOutputsToSave.push(...toolOutputs);

      const newAssistantMessage: ConversationMessage = {
        role: 'assistant',
        content: finalResponse,
        toolOutputs: allToolOutputsToSave.length > 0 ? [...allToolOutputsToSave] : undefined,
      };
      const updatedHistory = [...conversationHistory, newAssistantMessage];
      setConversationHistory(updatedHistory);

      // Track first response
      if (!hasTrackedResponse.current && conversationHistory.length === 1) {
        hasTrackedResponse.current = true;
        trackTryBloomResponseReceived({
          question: conversationHistory[0].content,
          questionType: initialSuggestedQuestions.includes(conversationHistory[0].content) ? 'suggested' : 'custom',
          responseTimeMs: Date.now() - questionStartTime.current,
          hasToolOutputs: toolOutputs.length > 0 || latestTodoOutput !== null,
        });
      }

      setStepState('response');
      resetQuery();
    }
  }, [
    finalResponse,
    isLoading,
    conversationHistory,
    initialSuggestedQuestions,
    toolOutputs,
    latestTodoOutput,
    resetQuery,
  ]);

  // Notify parent of state changes
  React.useEffect(() => {
    if (onStateChange) {
      onStateChange({
        hasAskedQuestion: stepState !== 'questions',
        hasResponse: stepState === 'response',
      });
    }
  }, [stepState, onStateChange]);

  // Common button styles for suggested questions
  const questionButtonStyles = css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${spacing.centi};
    padding: 16px 20px;
    background: ${colors.snow};
    border: 0.5px solid rgba(0, 0, 0, 0.2);
    border-radius: 24px;
    cursor: pointer;
    text-align: left;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    animation: ${fadeInUp} 0.3s ease-out;
    animation-fill-mode: both;

    &::after {
      content: '→';
      opacity: 0.5;
      transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
      flex-shrink: 0;
      color: ${colors.silver};
    }

    &:hover {
      background: rgba(247, 247, 248, 1);
      border-color: rgba(0, 0, 0, 0.12);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
      transform: translateY(-1px);

      &::after {
        transform: translateX(3px);
        opacity: 0.8;
        color: ${colors.petrolBlue};
      }
    }

    &:active {
      transform: translateY(0px);
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
    }

    html[data-theme='dark'] & {
      background: rgba(255, 255, 255, 0.02);
      border-color: rgba(255, 255, 255, 0.08);
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

      &::after {
        color: ${colors.cloud};
      }

      &:hover {
        background: rgba(255, 255, 255, 0.04);
        border-color: rgba(255, 255, 255, 0.12);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

        &::after {
          color: ${colors.skyBlue};
        }
      }
    }
  `;

  const questionTextStyles = css`
    ${fontStyles.dog}
    color: ${colors.petrolBlue};
    line-height: 1.4;
    flex: 1;

    html[data-theme='dark'] & {
      color: ${colors.sugar};
    }
  `;

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: ${spacing.deci};
      `}
    >
      {stepState === 'questions' && (
        <>
          {/* Initial suggested questions */}
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: ${spacing.centi};
            `}
          >
            {initialSuggestedQuestions.map((question, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleQuestionTap(question)}
                css={css`
                  ${questionButtonStyles}
                  animation-delay: ${index * 50}ms;
                `}
              >
                <span css={questionTextStyles}>{question}</span>
              </button>
            ))}
          </div>

          {/* Custom question input */}
          <div
            css={css`
              display: flex;
              gap: ${spacing.centi};
              margin-top: ${spacing.centi};
            `}
          >
            <input
              type="text"
              value={customQuestion}
              onChange={(e) => setCustomQuestion(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCustomSubmit()}
              placeholder="Ask anything..."
              css={css`
                flex: 1;
                padding: ${spacing.deci};
                border: 1px solid ${isDarkMode ? colors.tungsten : colors.cloud};
                border-radius: 12px;
                background: ${isDarkMode ? colors.liquorice : colors.snow} !important;
                color: ${isDarkMode ? colors.sugar : colors.liquorice};
                ${fontStyles.dog}

                &::placeholder {
                  color: ${colors.silver};
                }

                &:focus {
                  outline: none;
                  border-color: ${colors.petrolBlue};
                }
              `}
            />
            <button
              type="button"
              onClick={handleCustomSubmit}
              disabled={!customQuestion.trim()}
              css={css`
                padding: ${spacing.centi} ${spacing.deci};
                background: ${colors.petrolBlue};
                border: none;
                border-radius: 12px;
                color: ${colors.sugar};
                ${fontStyles.dog}
                cursor: pointer;
                opacity: ${customQuestion.trim() ? 1 : 0.5};
                transition: opacity 0.2s ease;

                &:hover:not(:disabled) {
                  opacity: 0.9;
                }
              `}
            >
              →
            </button>
          </div>
        </>
      )}

      {(stepState === 'loading' || stepState === 'response') && conversationHistory.length > 0 && (
        <>
          <TryBloomConversation
            isDarkMode={isDarkMode}
            messages={conversationHistory}
            isLoading={isLoading}
            progressText={progressText}
            streamingMessage={streamingMessage}
            currentResponse={finalResponse}
            toolOutputs={toolOutputs}
            latestTodoOutput={latestTodoOutput}
          />

          {/* Follow-up input (shown after response) */}
          {stepState === 'response' && (
            <div
              css={css`
                display: flex;
                gap: ${spacing.centi};
                margin-top: ${spacing.centi};
              `}
            >
              <input
                type="text"
                value={customQuestion}
                onChange={(e) => setCustomQuestion(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCustomSubmit()}
                placeholder="Ask a follow-up..."
                css={css`
                  flex: 1;
                  padding: ${spacing.deci};
                  border: 1px solid ${isDarkMode ? colors.tungsten : colors.cloud};
                  border-radius: 12px;
                  background: ${isDarkMode ? colors.liquorice : colors.snow} !important;
                  color: ${isDarkMode ? colors.sugar : colors.liquorice};
                  ${fontStyles.dog}

                  &::placeholder {
                    color: ${colors.silver};
                  }

                  &:focus {
                    outline: none;
                    border-color: ${colors.petrolBlue};
                  }
                `}
              />
              <button
                type="button"
                onClick={handleCustomSubmit}
                disabled={!customQuestion.trim()}
                css={css`
                  padding: ${spacing.centi} ${spacing.deci};
                  background: ${colors.petrolBlue};
                  border: none;
                  border-radius: 12px;
                  color: ${colors.sugar};
                  ${fontStyles.dog}
                  cursor: pointer;
                  opacity: ${customQuestion.trim() ? 1 : 0.5};
                  transition: opacity 0.2s ease;

                  &:hover:not(:disabled) {
                    opacity: 0.9;
                  }
                `}
              >
                →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TryBloomStep;
